import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Toolbar,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  Folder,
  Add,
  Settings,
  Help,
  Info,
  VideoLibrary,
  Analytics,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { RootState, useAppDispatch } from '../../store';

// Sidebar width
const DRAWER_WIDTH = 240;

// Navigation item type
interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  exact?: boolean;
  dividerAfter?: boolean;
}

const AppSidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  
  // Close sidebar on mobile when navigating
  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      dispatch(toggleSidebar());
    }
  };
  
  // Check if a route is active
  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };
  
  // Main navigation items - PROJECT FOCUSED
  const mainNavItems: NavItem[] = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      exact: true,
    },
    {
      text: 'Projects',
      icon: <Folder />,
      path: '/projects',
    },
    {
      text: 'Videos',
      icon: <VideoLibrary />,
      path: '/videos',
    },
    {
      text: 'Analytics',
      icon: <Analytics />,
      path: '/analytics-dashboard',
    },
  ];
  
  // Support navigation items
  const supportNavItems: NavItem[] = [
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/settings',
    },
    {
      text: 'Help Center',
      icon: <Help />,
      path: '/help',
    },
    {
      text: 'About',
      icon: <Info />,
      path: '/about',
    },
  ];
  
  // Render a navigation item
  const renderNavItem = (item: NavItem, index: number) => {
    const isActiveRoute = isActive(item.path, item.exact);
    
    return (
      <React.Fragment key={`nav-item-${index}`}>
        <ListItem
          button
          onClick={() => handleNavigate(item.path)}
          sx={{
            mb: 0.5,
            borderRadius: 1,
            mx: 1,
            color: isActiveRoute ? 'primary.main' : 'text.primary',
            backgroundColor: isActiveRoute ? 'rgba(255, 107, 53, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 107, 53, 0.05)',
            },
          }}
        >
          <ListItemIcon sx={{ color: isActiveRoute ? 'primary.main' : 'inherit', minWidth: 40 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItem>
        
        {item.dividerAfter && <Divider sx={{ my: 1.5 }} />}
      </React.Fragment>
    );
  };
  
  // Drawer content
  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <List sx={{ pt: 0 }}>
          {mainNavItems.map(renderNavItem)}
          
          <Box sx={{ mt: 2, mx: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleNavigate('/projects/create')}
              fullWidth
              sx={{ 
                bgcolor: '#FF6B35', // Nuclius orange
                color: '#000000', // Black text on orange
                '&:hover': { 
                  bgcolor: '#E55A2B' // Darker orange on hover
                } 
              }}
            >
              Create Project
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {supportNavItems.map(renderNavItem)}
        </List>
        
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Nuclius AI Platform
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Version 1.0.0
          </Typography>
        </Box>
      </Box>
    </>
  );
  
  return (
    <>
      {/* Mobile drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={() => dispatch(toggleSidebar())}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        // Desktop drawer
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default AppSidebar; 
